import React, { useCallback, useEffect, useState } from "react";

import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Typography,
} from "@mui/material";

import { ApplicationEndpoints } from "@generated/application";
import { apiFetch } from "@platform/http/apiClient";
import { Application } from "src/domains/application/types";

import {
  ENVIRONMENT_OPTIONS,
  manifestToYaml,
  type ManifestDiffResponse,
  type ManifestPreviewResponse,
  type ManifestVersionEntry,
} from "./manifest-preview-constants";
import { ManifestDiffView } from "./ManifestDiffView";
import { ManifestPreviewHeader } from "./ManifestPreviewHeader";
import { ManifestServicesSummary } from "./ManifestServicesSummary";
import { ManifestVersionSelector } from "./ManifestVersionSelector";
import { ManifestYamlViewer } from "./ManifestYamlViewer";

// ── Component ────────────────────────────────────────────────────────────────

interface ManifestPreviewTabProps {
  system: Application;
}

export function ManifestPreviewTab({
  system,
}: ManifestPreviewTabProps): React.ReactElement {
  const [preview, setPreview] = useState<ManifestPreviewResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [environment, setEnvironment] = useState<string>("production");
  const [copied, setCopied] = useState(false);
  const [versions, setVersions] = useState<ManifestVersionEntry[]>([]);
  const [selectedVersion, setSelectedVersion] = useState<number | null>(null);
  const [loadingVersion, setLoadingVersion] = useState(false);
  const [showDiffDialog, setShowDiffDialog] = useState(false);
  const [diffFrom, setDiffFrom] = useState<number | null>(null);
  const [diffTo, setDiffTo] = useState<number | null>(null);
  const [diffData, setDiffData] = useState<ManifestDiffResponse | null>(null);
  const [diffLoading, setDiffLoading] = useState(false);
  const [diffError, setDiffError] = useState<string | null>(null);

  const loadManifest = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiFetch<ManifestPreviewResponse>(
        `${ApplicationEndpoints.GET_APPLICATION_MANIFEST_PREVIEW.replace(":id", system.alias)}?environment=${encodeURIComponent(environment)}`,
      );
      setPreview(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load manifest");
    } finally {
      setLoading(false);
    }
  }, [system.alias, environment]);

  useEffect(() => {
    void loadManifest();
  }, [loadManifest]);

  // Load version list for the current environment
  useEffect(() => {
    async function loadVersions() {
      try {
        const data = await apiFetch<ManifestVersionEntry[]>(
          `${ApplicationEndpoints.GET_APPLICATION_MANIFEST_VERSIONS.replace(":id", system.alias)}?environment=${encodeURIComponent(environment)}`,
        );
        setVersions(Array.isArray(data) ? data : []);
      } catch {
        // Version listing is non-critical; silently fail
        setVersions([]);
      }
    }
    void loadVersions();
  }, [system.alias, environment]);

  const handleVersionChange = useCallback(
    async (version: number) => {
      setSelectedVersion(version);
      setLoadingVersion(true);
      try {
        const data = await apiFetch<ManifestPreviewResponse>(
          `${ApplicationEndpoints.GET_APPLICATION_MANIFEST_PREVIEW.replace(":id", system.alias)}?environment=${encodeURIComponent(environment)}&version=${version}`,
        );
        setPreview(data);
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            : "Failed to load manifest version",
        );
      } finally {
        setLoadingVersion(false);
      }
    },
    [system.alias, environment],
  );

  const handleOpenDiff = useCallback(() => {
    const latestVersion = versions[0]?.version;
    const previousVersion = versions[1]?.version;
    setDiffFrom(previousVersion ?? null);
    setDiffTo(latestVersion ?? null);
    setDiffData(null);
    setDiffError(null);
    setShowDiffDialog(true);
  }, [versions]);

  const handleFetchDiff = useCallback(async () => {
    if (diffFrom == null || diffTo == null) return;
    setDiffLoading(true);
    setDiffError(null);
    try {
      const data = await apiFetch<ManifestDiffResponse>(
        `${ApplicationEndpoints.GET_APPLICATION_MANIFEST_DIFF.replace(":id", system.alias)}?env=${encodeURIComponent(environment)}&from=${diffFrom}&to=${diffTo}`,
      );
      setDiffData(data);
    } catch (err) {
      setDiffError(err instanceof Error ? err.message : "Failed to load diff");
    } finally {
      setDiffLoading(false);
    }
  }, [system.alias, environment, diffFrom, diffTo]);

  const manifest = preview?.manifest ?? null;
  const resolved = preview?.resolved ?? null;

  const yamlContent = manifest ? manifestToYaml(manifest) : "";
  const serviceCount = manifest?.spec.services.length ?? 0;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(yamlContent);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const textarea = document.createElement("textarea");
      textarea.value = yamlContent;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleDownload = () => {
    const blob = new Blob([yamlContent], { type: "text/yaml" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${system.alias}-manifest.yaml`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Check if this is an adopted stack with no template — skip manifest API
  const isAdopted =
    !system.templateSlug &&
    (system.stackConfig as unknown as Record<string, unknown>)?._adopted ===
      true;

  if (isAdopted) {
    return (
      <Paper
        variant="outlined"
        sx={{ p: 4, borderRadius: 2, textAlign: "center" }}
      >
        <Box
          sx={{
            width: 60,
            height: 60,
            borderRadius: 2,
            margin: "0 auto",
            mb: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "rgba(59,130,246,0.08)",
          }}
        >
          <Typography
            sx={{ fontSize: 28, color: "primary.main", opacity: 0.5 }}
          >
            📄
          </Typography>
        </Box>
        <Typography variant="h6" fontWeight={600} gutterBottom>
          Manifest will be available after your first deploy
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 3, maxWidth: 500, mx: "auto" }}
        >
          The detected services will be rendered into Kubernetes manifests when
          you trigger a pipeline run.
        </Typography>
        <Button
          variant="outlined"
          color="primary"
          startIcon={<PlayArrowIcon />}
          sx={{ borderRadius: 1.5, textTransform: "none" }}
          onClick={() => {
            const url = new URL(window.location.href);
            url.searchParams.set("tab", "pipeline");
            window.history.pushState({}, "", url);
            window.dispatchEvent(new PopStateEvent("popstate"));
          }}
        >
          Run Pipeline
        </Button>
      </Paper>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert
        severity="error"
        action={
          <Button
            size="small"
            startIcon={<RefreshIcon />}
            onClick={loadManifest}
          >
            Retry
          </Button>
        }
      >
        {error}
      </Alert>
    );
  }

  if (!manifest) {
    return (
      <Alert severity="info">
        No manifest available for this stack. The manifest is assembled on first
        deploy.
      </Alert>
    );
  }

  return (
    <Stack spacing={2}>
      <ManifestPreviewHeader
        serviceCount={serviceCount}
        kind={manifest.kind}
        copied={copied}
        onRefresh={loadManifest}
        onCopy={handleCopy}
        onDownload={handleDownload}
      />

      {/* Environment selector */}
      <Stack direction="row" spacing={2} alignItems="center">
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Environment</InputLabel>
          <Select
            value={environment}
            label="Environment"
            onChange={(e) => setEnvironment(e.target.value)}
          >
            {ENVIRONMENT_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {resolved && (
          <Chip
            label={`Resolved for ${resolved.environment}`}
            size="small"
            color="primary"
            variant="outlined"
          />
        )}
      </Stack>

      <ManifestVersionSelector
        versions={versions}
        selectedVersion={selectedVersion}
        loadingVersion={loadingVersion}
        onVersionChange={handleVersionChange}
        onCompare={handleOpenDiff}
      />

      <ManifestYamlViewer yamlContent={yamlContent} />

      <ManifestServicesSummary manifest={manifest} resolved={resolved} />

      <ManifestDiffView
        open={showDiffDialog}
        onClose={() => setShowDiffDialog(false)}
        versions={versions}
        diffFrom={diffFrom}
        diffTo={diffTo}
        diffData={diffData}
        diffLoading={diffLoading}
        diffError={diffError}
        onFromChange={setDiffFrom}
        onToChange={setDiffTo}
        onFetchDiff={handleFetchDiff}
      />
    </Stack>
  );
}

export default ManifestPreviewTab;
